/**
 * FGA Unauthorized Component
 *
 * Displays an access denied screen when 401/403 authorization errors occur.
 * Provides options to request access or retry the operation.
 *
 * @packageDocumentation
 */

import {
  Component,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  inject,
  Input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { ErrorStateService } from '../../services/error-state.service';

/**
 * Component that displays an unauthorized/access denied screen.
 *
 * This component is shown when the user lacks permission to access FGA
 * resources (403) or when authentication has failed/expired (401).
 *
 * Features:
 * - Clear visual indication of access denial
 * - Error source identification (FGA, Auth0, etc.)
 * - Request Access action button
 * - Retry action button
 * - WCAG 2.2 compliant accessibility
 *
 * @example
 * ```html
 * <fga-unauthorized
 *   (requestAccess)="onRequestAccess()"
 *   (retry)="onRetry()">
 * </fga-unauthorized>
 * ```
 */
@Component({
  selector: 'fga-unauthorized',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ButtonModule],
  templateUrl: './fga-unauthorized.component.html',
  styleUrls: ['./fga-unauthorized.component.scss'],
})
export class FgaUnauthorizedComponent {
  // ---------------------------------------------------------------------------
  // Inputs
  // ---------------------------------------------------------------------------

  /**
   * Whether to show the error message from the API response.
   * Hidden by default for a cleaner UI.
   */
  @Input()
  showErrorMessage = false;

  /**
   * Whether to show the error source (FGA, Auth0, etc.).
   * Hidden by default for a cleaner UI.
   */
  @Input()
  showErrorSource = false;

  /**
   * Whether to show the action buttons (Request Access, Retry).
   * Hidden by default for a cleaner UI.
   */
  @Input()
  showActionButtons = false;

  // ---------------------------------------------------------------------------
  // Services
  // ---------------------------------------------------------------------------

  /**
   * Error state service for accessing error details
   */
  protected readonly errorState = inject(ErrorStateService);

  // ---------------------------------------------------------------------------
  // Outputs
  // ---------------------------------------------------------------------------

  /**
   * Emitted when the user clicks "Request Access".
   * Host application should handle navigation to access request form.
   */
  @Output()
  readonly requestAccess = new EventEmitter<void>();

  /**
   * Emitted when the user clicks "Retry".
   * Host application should trigger re-initialization.
   */
  @Output()
  readonly retry = new EventEmitter<void>();

  // ---------------------------------------------------------------------------
  // Protected Methods (Template)
  // ---------------------------------------------------------------------------

  /**
   * Handles Request Access button click.
   */
  protected onRequestAccess(): void {
    this.requestAccess.emit();
  }

  /**
   * Handles Retry button click.
   */
  protected onRetry(): void {
    this.retry.emit();
  }

  /**
   * Determines if the error source should be displayed.
   * Only shows source for known sources (fga, auth0).
   */
  protected shouldShowSource(): boolean {
    const source = this.errorState.errorSource();
    return source !== null && source !== 'unknown';
  }

  /**
   * Gets formatted source name for display.
   */
  protected getSourceDisplay(): string {
    const source = this.errorState.errorSource();
    switch (source) {
      case 'fga':
        return 'FGA Authorization Service';
      case 'auth0':
        return 'Auth0 Identity Service';

      default:
        return '';
    }
  }
}
